import { useEffect, useState } from "react";
import { Incidents } from "../../../../service/models/complaint_model";
import { X } from "react-feather";
import {
  ComplaintsRelatedToWithSubCategory,
  IncidentStatus,
} from "../../../../utils/enums";
import ComplaintRelatedToSearchField from "../create/components/complaints_related_typeable_widget";
import { State } from "country-state-city";

interface EditIncidentModalProps {
  incident: Incidents | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (incident: Incidents) => void;
}

const EditIncidentModal: React.FC<EditIncidentModalProps> = ({
  incident,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Incidents>(
    incident || ({} as Incidents)
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (incident && isOpen) {
      setFormData({ ...incident });
    }
  }, [incident, isOpen]);

  const handleSubmit = () => {
    onSave({ ...formData });
    onClose();
  };

  const complaint_categories = Object.keys(
    ComplaintsRelatedToWithSubCategory
  ).sort();

  const allowEmptySubCategory = ["Multiverse"];

  const subCategories = (
    ComplaintsRelatedToWithSubCategory[formData.complaint_related_to] || []
  )
    .slice()
    .sort();

  const isSubCategoryRequired: boolean =
    !!formData.complaint_related_to &&
    !allowEmptySubCategory.includes(formData.complaint_related_to);

  const isRemarkValid = (remark: string): boolean => {
    if (!remark?.trim()) return false;

    const words = remark.trim().split(" ").filter(Boolean).length;
    console.log("words : ", words);

    return words >= 5;
  };

  const isSubmitDisabled = !isRemarkValid(formData.remark || "");

  const handleRemarkChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, remark: value });

    const words = value.trim().split(/\s+/).filter(Boolean).length;

    if (!value.trim()) {
      setError("Remark is required");
    } else if (words < 5) {
      setError(`Remark must have at least 5 words (${words}/5)`);
    } else {
      setError("");
    }
  };

  if (!isOpen || !incident) return null;

  return (
    <div id="incident_tracker_edit_incident_modal" className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Incident</h2>
          <button
            id="incident_tracker_edit_incident_modal_close_btn"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Complainant Number
            </label>
            <input
              id="incident_tracker_edit_complainant_number"
              type="text"
              value={formData.complainant_number}
              onChange={(e) =>
                setFormData({ ...formData, complainant_number: e.target.value })
              }
              className="w-full border rounded px-3 py-2"
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Complainant Name
            </label>
            <input
              id="incident_tracker_edit_complainant_name"
              type="text"
              value={formData.complainant_name}
              onChange={(e) =>
                setFormData({ ...formData, complainant_name: e.target.value })
              }
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              id="incident_tracker_edit_status"
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full border rounded px-3 py-2"
            >
              {Object.values(IncidentStatus).map((_status, i) => (
                <option key={i} value={_status}>
                  {_status}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Organization
            </label>
            <input
              id="incident_tracker_edit_complainant_org"
              type="text"
              value={formData.complainant_org}
              onChange={(e) =>
                setFormData({ ...formData, complainant_org: e.target.value })
              }
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">State</label>
            <select
              id="incident_tracker_edit_complainant_state"
              value={formData.complainant_state || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  complainant_state: e.target.value,
                })
              }
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select a state</option>
              {State.getStatesOfCountry("IN").map((state) => (
                <option key={state.isoCode} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <ComplaintRelatedToSearchField
              label="Complaint related to"
              value={formData.complaint_related_to}
              options={complaint_categories}
              onChange={(value) => {
                setFormData((c: any) => ({
                  ...c,
                  complaint_related_to: value,
                  subcategory_of_related_to: "",
                }));
              }}
              onClear={() => {
                setFormData((c: any) => ({
                  ...c,
                  complaint_related_to: "",
                  subcategory_of_related_to: "",
                }));
              }}
              id="incident_tracker_edit_complaint_related_to"
              name="related_to"
              disabled={false}
              required={true}
              showValidationError={true}
              customErrorMessage={
                formData.complaint_related_to &&
                !complaint_categories.includes(formData.complaint_related_to)
                  ? `Please reselect previous category: ${formData.complaint_related_to}`
                  : undefined
              }
            />
          </div>
          <div>
            <ComplaintRelatedToSearchField
              label="Sub Category of related to"
              value={formData.subcategory_of_related_to || ""}
              options={subCategories}
              onChange={(value) => {
                setFormData((c: any) => ({
                  ...c,
                  subcategory_of_related_to: value,
                }));
              }}
              id="incident_tracker_edit_subcategory_of_related_to"
              name="subcategory_of_related_to"
              disabled={subCategories.length === 0}
              required={isSubCategoryRequired}
              showValidationError={true}
              customErrorMessage={
                isSubCategoryRequired &&
                formData.subcategory_of_related_to &&
                !subCategories.includes(formData.subcategory_of_related_to)
                  ? `Please reselect previous sub-category: ${formData.subcategory_of_related_to}`
                  : undefined
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Remark <RequiredMark />
            </label>
            <textarea
              id="incident_tracker_edit_remark"
              required
              value={formData.remark}
              onChange={handleRemarkChange}
              className={`w-full border rounded px-3 py-2 h-24 ${
                error ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Please provide a detailed remark with at least 5 words..."
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              id="incident_tracker_edit_cancel_btn"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              id="incident_tracker_edit_save_btn"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className={`px-4 py-2 rounded transition-all duration-200 ${
                isSubmitDisabled
                  ? "bg-blue-300 text-white opacity-50 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
              title={
                isSubmitDisabled
                  ? "Please provide a remark with at least 5 words/spaces"
                  : "Save changes"
              }
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RequiredMark = () => (
  <>
    <span className="text-red-500">*</span>
  </>
);
export default EditIncidentModal;
